import { useMemo } from 'react';
import { useGetMarketplacePaymentLinesWithSales } from 'src/contexts/MarketplacePaymentDataContext';
import { useGetMarketplacePaymentLinesWithSalesByFilters } from 'src/contexts/MarketplacePaymentDataContext/useGetMarketplacePaymentLinesWithSalesByFilters';
import { useGetCatalogBySaleIds } from 'src/hooks/api/useGetCatalogBySaleIds';
import {
  Event,
  MarketplacePaymentLineWithSaleInfo,
  Venue,
} from 'src/WebApiController';

export interface MarketplacePaymentLineWithSaleAndEventInfo
  extends MarketplacePaymentLineWithSaleInfo {
  event: Event | undefined;
  venue: Venue | undefined;
}

interface MarketplacePaymentLineTableDataResponse {
  data: MarketplacePaymentLineWithSaleAndEventInfo[];
  isLoading: boolean;
  error: Error | null;
}

export const useGetMarketplacePaymentLineTableData = (
  marketplacePaymentLineId: number | undefined,
  hasMarketplacePaymentLineFilterFeature: boolean
): MarketplacePaymentLineTableDataResponse => {
  const { paymentLineQuery: defaultPaymentLineQuery } =
    useGetMarketplacePaymentLinesWithSales(
      marketplacePaymentLineId ?? 0,
      hasMarketplacePaymentLineFilterFeature
    );

  const { paymentLineQuery: paymentLineQueryWithFilters } =
    useGetMarketplacePaymentLinesWithSalesByFilters(
      marketplacePaymentLineId ?? 0,
      !hasMarketplacePaymentLineFilterFeature
    );

  const paymentLineQuery = hasMarketplacePaymentLineFilterFeature
    ? paymentLineQueryWithFilters
    : defaultPaymentLineQuery;

  const {
    isLoading: isLoadingMktPaymentLines,
    error: mktPaymentLinesError,
    data: marketplacePaymentData,
  } = paymentLineQuery;

  const saleIds = useMemo(() => {
    return (marketplacePaymentData ?? [])
      .map((mktPaymentLine) => mktPaymentLine.saleId)
      .filter((s) => s != null) as number[];
  }, [marketplacePaymentData]);

  const {
    isLoading: isLoadingEventCatalog,
    error: eventCatalogError,
    data: catalog,
  } = useGetCatalogBySaleIds(saleIds);

  const isLoading = isLoadingEventCatalog || isLoadingMktPaymentLines;

  const data = useMemo(() => {
    if (isLoading || !catalog) {
      return [];
    }

    return (marketplacePaymentData ?? []).map(
      (mktPaymentLine): MarketplacePaymentLineWithSaleAndEventInfo => {
        const event = catalog.events[mktPaymentLine.viagEvId ?? '']?.event;
        return {
          ...mktPaymentLine,
          event,
          venue: catalog.venues[event?.venId],
        };
      }
    );
  }, [catalog, isLoading, marketplacePaymentData]);

  return {
    isLoading,
    error: eventCatalogError || mktPaymentLinesError,
    data,
  };
};
